import * as React from "react";
import { RouterLink as NextLink } from "../../router";
import { Card, CardContent } from "../../card";
import { Button } from "../../button";
import { Icon } from "../../icon";
import { cn } from "../../utils";
import type { Intent } from "../../tokens/intents";

/**
 * HealthContext — the annotated health-trend section that sits below the Focus
 * list on a dashboard. For each focus area it pairs a calm health sparkline
 * (line + faint area + dashed target) with a log of WHAT happened: a cause
 * (what made it dip), a fix (what resolved it), and the resolution currently in
 * flight (pending). The approved /styleguide/overview prototype, wired to real
 * data — health samples (migration v81) + audit-derived annotations.
 *
 * Presentational only. Renders nothing when no area has ≥2 samples (the series
 * isn't meaningful yet — it fills in forward as samples accrue).
 */

export type HealthAnnotationKind = "cause" | "fix" | "pending";

export interface HealthAnnotation {
  kind: HealthAnnotationKind;
  /** Human label for when it happened ("3 wks ago", "now"). */
  when: string;
  text: string;
  /** Sample date (`YYYY-MM-DD`) this annotation sits on; maps it onto the line.
   *  Omit for `pending` — it pins to the latest point. */
  on?: string;
}

export interface HealthArea {
  key: string;
  label: string;
  icon?: string;
  /** Current blended health 0–100. */
  health: number;
  intent: Intent;
  /** Week-over-week delta (negative = falling). */
  trend?: number;
  /** Oldest → newest samples. */
  history: { on: string; health: number }[];
  annotations: HealthAnnotation[];
  /** "Open <area>" target. */
  href?: string;
}

export interface HealthContextProps {
  areas: HealthArea[];
  title?: string;
  className?: string;
}

const TARGET = 70;

const LINE: Record<Intent, string> = {
  success: "text-success",
  warning: "text-warning",
  danger: "text-danger",
  info: "text-info",
  neutral: "text-muted-foreground",
};
const AREA_FILL: Record<Intent, string> = {
  success: "fill-success/10",
  warning: "fill-warning/10",
  danger: "fill-danger/10",
  info: "fill-info/10",
  neutral: "fill-muted/30",
};
const ANN_DOT: Record<HealthAnnotationKind, string> = {
  cause: "bg-danger",
  fix: "bg-success",
  pending: "bg-muted-foreground",
};
const ANN_TEXT: Record<HealthAnnotationKind, string> = {
  cause: "text-danger",
  fix: "text-success",
  pending: "text-muted-foreground",
};
const ANN_ICON: Record<HealthAnnotationKind, string> = {
  cause: "arrow-down",
  fix: "arrow-up",
  pending: "clock",
};

/** Index of the history point an annotation sits on: the sample nearest the
 *  annotation's date (pending / undated → the latest point). */
function annIndex(history: { on: string }[], a: HealthAnnotation): number {
  if (!a.on) return history.length - 1;
  const target = new Date(a.on).getTime();
  if (Number.isNaN(target)) return history.length - 1;
  let best = history.length - 1;
  let bestDiff = Infinity;
  history.forEach((p, i) => {
    const diff = Math.abs(new Date(p.on).getTime() - target);
    if (diff < bestDiff) {
      bestDiff = diff;
      best = i;
    }
  });
  return best;
}

export function HealthSparkline({
  area,
  height = 60,
  labels = false,
}: {
  area: HealthArea;
  height?: number;
  /** At the larger (≥80px) size, pin a "70 target" caption on the dashed line and
   *  the latest value at the polyline's terminal. Off for the small rail/inline uses. */
  labels?: boolean;
}) {
  const data = area.history;
  if (data.length < 2) return null;
  const maxX = data.length - 1;
  const line = data.map((p, i) => `${i},${100 - p.health}`).join(" ");
  const area2 = `0,100 ${line} ${maxX},100`;
  const last = data[maxX]?.health ?? 0;
  return (
    <div className="relative w-full" style={{ height }}>
      <svg
        viewBox={`0 0 ${maxX} 100`}
        preserveAspectRatio="none"
        className="absolute inset-0 h-full w-full"
        aria-hidden
      >
        <line
          x1="0"
          y1={100 - TARGET}
          x2={maxX}
          y2={100 - TARGET}
          stroke="currentColor"
          className="text-foreground/15"
          strokeDasharray="3 3"
          strokeWidth="1"
          vectorEffect="non-scaling-stroke"
        />
        <polygon points={area2} className={AREA_FILL[area.intent]} />
        <polyline
          points={line}
          fill="none"
          stroke="currentColor"
          className={LINE[area.intent]}
          strokeWidth="1.5"
          strokeLinejoin="round"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />
      </svg>
      {labels && (
        <>
          <span
            className="pointer-events-none absolute right-0 -translate-y-1/2 type-subtext tabular-nums text-muted-foreground/60"
            style={{ top: `${100 - TARGET}%` }}
          >
            {TARGET} target
          </span>
          <span
            className={cn(
              "pointer-events-none absolute right-0 -translate-y-1/2 rounded-sm bg-card px-1 type-subtext font-medium tabular-nums",
              LINE[area.intent],
            )}
            style={{ top: `${Math.min(Math.max(100 - last, 8), 88)}%` }}
          >
            {last}
          </span>
        </>
      )}
      {area.annotations.map((a, i) => {
        const idx = annIndex(data, a);
        return (
          <span
            key={i}
            className={cn(
              "absolute rounded-full ring-2 ring-card",
              height >= 56 ? "size-2" : "size-1.5",
              ANN_DOT[a.kind],
            )}
            style={{
              left: `${(idx / maxX) * 100}%`,
              top: `${100 - (data[idx]?.health ?? 0)}%`,
              transform: "translate(-50%, -50%)",
            }}
            title={`${a.when} — ${a.text}`}
          />
        );
      })}
    </div>
  );
}

function AreaHealthCard({ area }: { area: HealthArea }) {
  return (
    <Card className="gap-0 overflow-hidden py-0">
      <CardContent className="flex flex-col gap-3 px-5 py-4">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          {area.icon ? (
            <Icon name={area.icon} size="sm" className="self-center text-muted-foreground" />
          ) : null}
          <span className="type-body font-medium text-foreground">{area.label}</span>
          <span className={cn("type-body font-medium tabular-nums", LINE[area.intent])}>
            {area.health}
          </span>
          {area.trend != null && area.trend < 0 ? (
            <span className="inline-flex items-center gap-0.5 type-subtext tabular-nums text-muted-foreground">
              <Icon name="arrow-down" size="xs" />
              {Math.abs(area.trend)}
            </span>
          ) : null}
          {area.href ? (
            <Button asChild size="xs" variant="ghost" className="ml-auto -mr-2 shrink-0">
              <NextLink href={area.href}>
                Open {area.label}
                <Icon name="arrow-right" size="xs" />
              </NextLink>
            </Button>
          ) : null}
        </div>
        <div className="grid items-center gap-x-6 gap-y-4 sm:grid-cols-2">
          <HealthSparkline area={area} />
          <div className="flex flex-col gap-1.5">
            {area.annotations.map((a, i) => (
              <div key={i} className="flex items-start gap-2 type-subtext">
                <Icon name={ANN_ICON[a.kind]} size="xs" className={cn("mt-0.5 shrink-0", ANN_TEXT[a.kind])} />
                <span className="text-muted-foreground">
                  <span className="text-foreground">{a.when}</span> — {a.text}
                </span>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export function HealthContext({ areas, title = "Health & what happened", className }: HealthContextProps) {
  const chartable = areas.filter((a) => a.history.length >= 2);
  if (chartable.length === 0) return null;
  return (
    <section className={cn("flex flex-col gap-2", className)}>
      <div className="flex flex-wrap items-baseline gap-x-2 px-1">
        <span className="type-body font-medium text-foreground">{title}</span>
        <span className="type-subtext text-muted-foreground">why each area dipped — and what resolved it</span>
      </div>
      <div className="flex flex-col gap-3">
        {chartable.map((a) => (
          <AreaHealthCard key={a.key} area={a} />
        ))}
      </div>
    </section>
  );
}
